
import { useState } from 'react';
import Card from '../base/Card';

interface ChartData {
  label: string;
  value: number;
  color: string;
  details?: { [key: string]: number };
}

interface InteractiveChartProps {
  title: string;
  data: ChartData[];
  type: 'income' | 'expense' | 'balance';
}

export default function InteractiveChart({ title, data, type }: InteractiveChartProps) {
  const [chartType, setChartType] = useState<'pie' | 'bar'>('pie');
  const [selectedItem, setSelectedItem] = useState<ChartData | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  const handleItemClick = (item: ChartData) => {
    if (item.details) {
      setSelectedItem(item);
      setShowDetails(true);
    }
  };
  
  const closeDetails = () => {
    setShowDetails(false);
    setSelectedItem(null);
  };
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount);
  };
  
  return (
    <>
      <Card className="h-full">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-800">{title}</h3>
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setChartType('pie')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                chartType === 'pie' 
                  ? 'bg-white text-emerald-600 shadow-sm' 
                  : 'text-gray-600 hover:text-emerald-600'
              }`}
            >
              <i className="ri-pie-chart-line mr-1"></i>
              Torta
            </button>
            <button
              onClick={() => setChartType('bar')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                chartType === 'bar' 
                  ? 'bg-white text-emerald-600 shadow-sm' 
                  : 'text-gray-600 hover:text-emerald-600'
              }`}
            >
              <i className="ri-bar-chart-line mr-1"></i>
              Barras
            </button>
          </div>
        </div>
        
        {chartType === 'pie' ? (
          <div className="flex flex-col lg:flex-row items-center gap-8">
            <div className="relative w-64 h-64">
              <svg viewBox="0 0 200 200" className="w-full h-full transform -rotate-90">
                {data.map((item, index) => {
                  const percentage = (item.value / total) * 100;
                  const angle = (percentage / 100) * 360;
                  const startAngle = data.slice(0, index).reduce((sum, prev) => sum + (prev.value / total) * 360, 0);
                  
                  const x1 = 100 + 80 * Math.cos((startAngle * Math.PI) / 180);
                  const y1 = 100 + 80 * Math.sin((startAngle * Math.PI) / 180);
                  const x2 = 100 + 80 * Math.cos(((startAngle + angle) * Math.PI) / 180);
                  const y2 = 100 + 80 * Math.sin(((startAngle + angle) * Math.PI) / 180);
                  
                  const largeArcFlag = angle > 180 ? 1 : 0;
                  
                  return (
                    <path
                      key={index}
                      d={`M 100 100 L ${x1} ${y1} A 80 80 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                      fill={item.color}
                      className="cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => handleItemClick(item)}
                    />
                  );
                })}
              </svg>
            </div>
            
            <div className="flex-1 space-y-3">
              {data.map((item, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => handleItemClick(item)}
                >
                  <div className="flex items-center">
                    <div 
                      className="w-4 h-4 rounded-full mr-3"
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <span className="font-medium text-gray-700">{item.label}</span>
                    {item.details && <i className="ri-arrow-right-s-line ml-2 text-gray-400"></i>}
                  </div>
                  <span className="font-bold text-gray-800">{formatCurrency(item.value)}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {data.map((item, index) => {
              const percentage = (item.value / total) * 100;
              return (
                <div key={index} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">{item.label}</span>
                    <span className="font-bold text-gray-800">{formatCurrency(item.value)}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="h-3 rounded-full transition-all duration-500 cursor-pointer hover:opacity-80"
                      style={{ 
                        width: `${percentage}%`, 
                        backgroundColor: item.color 
                      }}
                      onClick={() => handleItemClick(item)}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold text-gray-700">Total:</span>
            <span className={`text-xl font-bold ${
              type === 'income' ? 'text-emerald-600' : 
              type === 'expense' ? 'text-red-600' : 
              total >= 0 ? 'text-emerald-600' : 'text-red-600'
            }`}>
              {formatCurrency(total)}
            </span>
          </div>
        </div>
      </Card>
      
      {/* Modal de detalles */}
      {showDetails && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-xl font-bold text-gray-800">
                Desglose: {selectedItem.label}
              </h4>
              <button
                onClick={closeDetails}
                className="text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="space-y-3">
              {selectedItem.details && Object.entries(selectedItem.details).map(([key, value]) => (
                <div key={key} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <span className="font-medium text-gray-700">{key}</span>
                  <span className="font-bold text-gray-800">{formatCurrency(value)}</span>
                </div>
              ))}
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-700">Total:</span>
                <span className="text-xl font-bold text-emerald-600">
                  {formatCurrency(selectedItem.value)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
